import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subscriber, tap } from 'rxjs';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { StoreConfig } from '@services/setting-services/models';
import { map } from 'rxjs/operators';
import { CachingApiService } from '@services/core-services/api-cache.service';
import { Rule, ShippingCostPolicy } from './models/shipping-cost-rules';

interface ShippingCostPolicyResponse {
  content: {
    rules: Rule[];
  };
}

@Injectable({ providedIn: 'root' })
export class StoreSettingsService {
  private config = new BehaviorSubject<StoreConfig | undefined>({
    is_customer_group_enabled: false,
    is_kyc_enabled: false,
    loaded: false,
    channels: [],
    subscription: undefined,
    brand: undefined,
    customer_groups: [],
    tax: undefined,
    shipping: undefined,
  });
  private eventSource: EventSource | undefined;

  constructor(
    private zone: NgZone,
    private readonly cacheApi: CachingApiService
  ) {}

  getConfig() {
    return this.config.asObservable();
  }

  getStoreConfigSnapshot(): StoreConfig {
    return this.config.getValue() as StoreConfig;
  }

  loadStoreConfig(): Observable<StoreConfig> {
    const config = this.config.getValue();
    return config?.loaded
      ? (this.config.asObservable() as Observable<StoreConfig>)
      : this.getStoreConfig().pipe(
          tap((config: any) => this.config.next({ ...config, loaded: true }))
        );
  }

  getStoreConfig(): Observable<StoreConfig> {
    return this.cacheApi
      .getWithCache<any>('store/config')
      .pipe(map((response: any) => response.content['store_config']));
  }

  createShippingCostPolicy(payload: ShippingCostPolicy): Observable<Rule[]> {
    return this.cacheApi
      .post<ShippingCostPolicy, ShippingCostPolicyResponse>('store/shipping-cost-policy', payload)
      .pipe(map((response: ShippingCostPolicyResponse) => response.content.rules));
  }

  deleteShippingCostPolicy(id: string): Observable<void> {
    return this.cacheApi.delete(`store/shipping-cost-policy/${id}`);
  }

  getShippingCostPolicies(): Observable<Rule[]> {
    return this.cacheApi
      .get<ShippingCostPolicyResponse>('store/shipping-cost-policy')
      .pipe(map((response: ShippingCostPolicyResponse) => response.content.rules));
  }

  updateStoreConfig(payload: Partial<StoreConfig>): Observable<StoreConfig> {
    const { customer_groups, ...rest } = payload;
    const params: any = {
      ...rest,
    };

    // If id is not defined/empty (new customer group) for single customer group than ignore id property
    if (customer_groups) {
      params.customer_groups = (customer_groups || []).map(cg => {
        const { id, ...cgr } = cg;
        return cg.id ? cg : cgr;
      });
    }

    return this.cacheApi
      .post('store/config', params)
      .pipe(tap((response: any) => this.config.next(response.content.store_config)));
  }

  refreshConfig(config: StoreConfig) {
    this.config.next(config);
  }

  /**
   * Method for creation of the EventSource instance
   */
  getEventSource(): EventSource {
    const url = [this.cacheApi.getBaseUrl(), 'store/sse/global-state'].join('/');
    const bearer = this.cacheApi.getAccessToken();
    return new EventSourcePolyfill(url, {
      headers: {
        Accept: '*/*',
        Authorization: 'Bearer ' + bearer,
      },
    });
  }

  /**
   * Method for establishing connection and subscribing to events from SSE
   * @param eventNames - all event names except error (listens by default)
   */
  connectToServerSentEvents(eventNames: string[] = []): Observable<Event> {
    this.eventSource = this.getEventSource();

    return new Observable((subscriber: Subscriber<Event>) => {
      this.eventSource!.onerror = error => {
        this.zone.run(() => subscriber.error(error));
      };

      this.eventSource!.onmessage = data => {
        this.zone.run(() => subscriber.next(data));
      };

      eventNames.forEach((event: string) => {
        this.eventSource!.addEventListener(event, data => {
          this.zone.run(() => subscriber.next(data));
        });
      });
    });
  }

  /**
   * Method for closing the connection
   */
  disconnectServerSentEvents(): void {
    if (!this.eventSource) {
      return;
    }

    this.eventSource.close();
    this.eventSource = undefined;
  }
}
